import { inject } from '@angular/core';
import { filter, switchMap, take } from 'rxjs';
import { HttpInterceptorFn } from '@angular/common/http';

/**
 * utilities
 */
import { JwtUtils } from '@core/utilities';
import { AppHttpUtils } from '../../../utilities';

/**
 * consts
 */
import { FORCE_REFRESH_TOKEN_CONTEXT } from '../bypass';

/**
 * identity
 */
import { AUTH_SESSION_MANAGEMENT } from '@identity/authentication';

/**
 * JWT HTTP Interceptor
 * 
 * @function jwtInterceptor
 * @description Automatically adds JWT Bearer token to HTTP requests when user is logged in and has a valid token.
 * Checks authentication state and injects the access token into the Authorization header.
 * 
 * @param {HttpRequest<any>} req - The outgoing HTTP request
 * @param {HttpHandlerFn} next - The next interceptor in the chain
 * @returns {Observable<HttpEvent<any>>} The HTTP response with or without JWT token
 * 
 * @example
 * ```typescript
 * // Automatically adds Bearer token to requests when user is logged in
 * // GET /api/users -> Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export const jwtInterceptor: HttpInterceptorFn = (req, next) => {

  // Inject authentication services
  const _authSessionManagement = inject(AUTH_SESSION_MANAGEMENT);

  // Check if request has bypass JWT header
  const _isHasBypassJwtHeader = AppHttpUtils.isHasBypassJwt(req);
  
  // Check if request has bypass app request header
  const _isHasBypassAppRequest = AppHttpUtils.isHasBypassAppRequest(req);

  // Check if user is logged in and has a token
  if (_isHasBypassJwtHeader || _isHasBypassAppRequest || !_authSessionManagement.accessToken) {
    return next(req);
  }

  const waitRefreshDone$ = _authSessionManagement.refreshing$.pipe(
    filter(is => !is),
    take(1)
  );
  
  // Refresh token if needed
  // If the token is refreshing, wait for it to complete before sending the request
  return waitRefreshDone$.pipe(
    switchMap(() => _authSessionManagement.accessToken$.pipe(take(1))),
    // here token is definitely the last value
    switchMap(token => {

      // if token is not found, return next request
      if (!token) return next(req); 
      
      // Create JWT utilities
      const _jwtUtilsInstance = new JwtUtils(token);

      // check if token is expired or will expire within 120 seconds
      const needRefresh = _jwtUtilsInstance.isExpiredOrSkew(120);

      // if token is expired or will expire within 120 seconds, set context to force refresh token
      if (needRefresh) {
        return _authSessionManagement.refreshAccessTokenShared().pipe(
          switchMap((newToken) => next(req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } })))
        );
      }

      // clone request and add authorization header
      let authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
      
      return next(authReq);

    })
  )
  
};
